/**
 * Wrapper fetch pour les 4 endpoints publics du dépôt sécurisé médical.
 *
 * On n'utilise PAS l'instance axios `app/client` parce qu'elle ajoute
 * automatiquement le Bearer token de l'utilisateur connecté côté Espace Pro.
 * Le souscripteur qui arrive sur cette page n'est pas authentifié — la route
 * doit donc partir avec un header Authorization vide.
 *
 * Base API : /api/public/emprunteur/depot-securise
 */

import { baseURL } from 'app/client';
import type {
  DepotDeposerResultatResponse,
  DepotOperationResponse,
  DepotSecuriseEtatResponse,
  DepotSecuriseInfosResponse
} from '../types';

const PUBLIC_BASE = `${baseURL}/api/public/emprunteur/depot-securise`;

/**
 * Codes d'erreur métier exposés par le back. À garder synchronisé avec la
 * doc back. Utilisé pour switcher l'UI (ex: routage écran "Bloqué" sur
 * MEDICAL_FORM_TOKEN_BLOCKED, message simplifié sur erreurs de format).
 */
export const DepotErrorCode = {
  // Token / accès — terminaux : la page doit basculer sur l'écran d'erreur.
  TokenInvalid: 'MEDICAL_FORM_TOKEN_INVALID',
  TokenExpired: 'MEDICAL_FORM_TOKEN_EXPIRED',
  TokenBlocked: 'MEDICAL_FORM_TOKEN_BLOCKED',
  SubscriberNotFound: 'MEDICAL_FORM_SUBSCRIBER_NOT_FOUND',
  // Authentification DDN — re-saisie possible (sauf TokenBlocked ci-dessus).
  DobRequired: 'MEDICAL_FORM_DOB_REQUIRED',
  DobIncorrect: 'MEDICAL_FORM_DOB_INCORRECT',
  // Dépôt fichier — l'utilisateur peut retenter avec un autre fichier.
  FileEmpty: 'MEDICAL_FORM_FILE_EMPTY',
  FileTooLarge: 'MEDICAL_FORM_FILE_TOO_LARGE',
  FileNameMissing: 'MEDICAL_FORM_FILE_NAME_MISSING',
  FileExtensionForbidden: 'MEDICAL_FORM_FILE_EXTENSION_FORBIDDEN',
  FileMimeForbidden: 'MEDICAL_FORM_FILE_MIME_FORBIDDEN',
  FileContentInvalid: 'MEDICAL_FORM_FILE_CONTENT_INVALID',
  FileExtContentMismatch: 'MEDICAL_FORM_FILE_EXT_CONTENT_MISMATCH',
  // Suppression — fichier inconnu pour cet emprunteur (anti-énumération).
  FileNotFound: 'MEDICAL_FORM_FILE_NOT_FOUND',
  // Confirmation.
  NoFileUploaded: 'MEDICAL_FORM_NO_FILE_UPLOADED',
  AlreadyConfirmed: 'MEDICAL_FORM_ALREADY_CONFIRMED'
} as const;
export type DepotErrorCodeValue =
  (typeof DepotErrorCode)[keyof typeof DepotErrorCode];

/**
 * Erreur métier renvoyée par le back. Porte le code stable (pour switcher
 * l'UI) et le message FR (pour affichage par défaut), plus une éventuelle
 * payload structurée (ex: `remainingAttempts`).
 */
export class DepotBusinessError extends Error {
  constructor(
    /** Code stable côté back, voir `DepotErrorCode`. Vide si le back en oublie. */
    public readonly code: string,
    /** Texte FR prêt à afficher (fallback si on ne switche pas sur le code). */
    public readonly userMessage: string,
    /** Payload structuré (ex: { remainingAttempts: 2 }). */
    public readonly data?: Record<string, unknown>
  ) {
    super(userMessage);
    this.name = 'DepotBusinessError';
  }
}

/**
 * Vrai si la chaîne ressemble à un identifiant stable (MAJ + underscores)
 * type `MEDICAL_FORM_DOB_INCORRECT`, par opposition à une phrase utilisateur.
 *
 * Sert à distinguer les deux formats back qu'on rencontre en pratique :
 * 1. Nouveau format (spec à venir) :
 *      error.code    = "MEDICAL_FORM_DOB_INCORRECT"        ← identifiant
 *      error.message = "Date de naissance incorrecte. ..." ← texte FR
 * 2. Ancien format ABP (encore en place sur certains endpoints) :
 *      error.code    = "Date de naissance incorrecte. ..." ← texte FR
 *      error.message = "Une erreur interne s'est produite lors de votre demande!"
 *                                                          ← wrapper inutile
 *
 * Le format est détecté par la forme de `error.code`, pas par la présence
 * de telle ou telle clé — comme ça les deux marchent sans configuration et
 * la migration back peut se faire endpoint par endpoint sans rien casser.
 */
function looksLikeStableCode(s: string): boolean {
  return /^[A-Z][A-Z0-9_]*$/.test(s);
}

/**
 * Extrait {code, message, data} d'une réponse en erreur.
 *
 * Supporte les deux formats ABP back décrits dans `looksLikeStableCode`,
 * plus quelques variantes simples (`errorMessage` / `message` à la racine)
 * pour les autres endpoints éventuels.
 */
async function parseError(response: Response): Promise<{
  code: string;
  message: string;
  data?: Record<string, unknown>;
}> {
  let code = '';
  let message = '';
  let data: Record<string, unknown> | undefined;

  try {
    const body = await response.json();
    const errCode =
      typeof body?.error?.code === 'string' ? body.error.code.trim() : '';
    const errMessage =
      typeof body?.error?.message === 'string'
        ? body.error.message.trim()
        : '';

    if (errCode && looksLikeStableCode(errCode)) {
      // Nouveau format : code = identifiant, message = texte FR.
      code = errCode;
      message = errMessage;
    } else if (errCode) {
      // Ancien format ABP : code = texte FR (on l'utilise), message =
      // wrapper générique (on l'ignore).
      message = errCode;
    } else if (errMessage) {
      message = errMessage;
    }

    if (!message) {
      if (
        typeof body?.errorMessage === 'string' &&
        body.errorMessage.trim()
      ) {
        message = body.errorMessage;
      } else if (typeof body?.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    }

    if (body?.error?.data && typeof body.error.data === 'object') {
      data = body.error.data as Record<string, unknown>;
    }
  } catch {
    /* JSON parse error → on retombe sur le message générique */
  }

  if (!message) {
    message = 'Une erreur est survenue. Veuillez réessayer.';
  }
  return { code, message, data };
}

/**
 * Mapping code → texte affiché à l'utilisateur.
 *
 * On ne mappe que les codes pour lesquels on veut un texte différent du
 * `message` du back (cas où on simplifie 4 codes distincts en une seule
 * phrase, ou cas où on veut être plus prescriptif). Pour les autres, on
 * renvoie le message du back tel quel — il est déjà localisé FR.
 */
export function formatDepotErrorMessage(error: DepotBusinessError): string {
  switch (error.code) {
    // Le back distingue extension / MIME / magic bytes / mismatch, mais
    // pour l'utilisateur c'est un seul concept : "mauvais format de fichier".
    case DepotErrorCode.FileExtensionForbidden:
    case DepotErrorCode.FileMimeForbidden:
    case DepotErrorCode.FileContentInvalid:
    case DepotErrorCode.FileExtContentMismatch:
      return 'Seuls les fichiers PDF, JPG ou PNG sont acceptés.';
    case DepotErrorCode.FileTooLarge:
      return 'Fichier trop volumineux (10 Mo max).';
    case DepotErrorCode.FileEmpty:
      return 'Le fichier est vide.';
    case DepotErrorCode.FileNameMissing:
      return 'Le fichier doit avoir un nom.';
    case DepotErrorCode.FileNotFound:
      return "Ce fichier n'existe plus. La liste a été rafraîchie.";
    case DepotErrorCode.NoFileUploaded:
      return 'Vous devez déposer au moins un fichier avant de confirmer.';
    case DepotErrorCode.AlreadyConfirmed:
      return 'Ce dépôt a déjà été confirmé.';
    default:
      // Pour DDN incorrecte, token expiré/bloqué, etc., le back a déjà
      // formulé le message proprement (avec le compteur de tentatives etc.).
      return error.userMessage;
  }
}

/**
 * Vrai pour les codes terminaux dont la page DOIT basculer sur l'écran
 * d'erreur dédié (l'utilisateur ne peut rien retenter, le token est mort).
 */
export function isTerminalDepotErrorCode(code: string): boolean {
  return (
    code === DepotErrorCode.TokenInvalid ||
    code === DepotErrorCode.TokenExpired ||
    code === DepotErrorCode.TokenBlocked ||
    code === DepotErrorCode.SubscriberNotFound
  );
}

/**
 * Vrai si le status HTTP signale une erreur métier dont le code+message
 * doivent être remontés tels quels à l'utilisateur. On groupe 400 + 403
 * parce que le back utilise 403 pour "DDN incorrecte sur action protégée"
 * (`/deposer`, `/confirmer`).
 */
function isBusinessStatus(status: number): boolean {
  return status === 400 || status === 403;
}

async function throwBusinessOrGeneric(response: Response): Promise<never> {
  const err = await parseError(response);
  if (isBusinessStatus(response.status)) {
    throw new DepotBusinessError(err.code, err.message, err.data);
  }
  throw new Error(err.message);
}

async function postJson<T>(
  url: string,
  body: Record<string, unknown>
): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    await throwBusinessOrGeneric(response);
  }
  return response.json() as Promise<T>;
}

/** 1. État du token : à appeler au montage de la page. */
export async function fetchDepotEtat(
  token: string
): Promise<DepotSecuriseEtatResponse> {
  const response = await fetch(`${PUBLIC_BASE}/${token}/etat`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    await throwBusinessOrGeneric(response);
  }
  return response.json() as Promise<DepotSecuriseEtatResponse>;
}

/** 2. Authentification par DDN (ISO yyyy-MM-dd). */
export async function authentifierDepot(
  token: string,
  dateNaissance: string
): Promise<DepotSecuriseInfosResponse> {
  return postJson<DepotSecuriseInfosResponse>(
    `${PUBLIC_BASE}/${token}/authentifier`,
    { dateNaissance }
  );
}

/**
 * 3. Dépôt d'un fichier. La DDN est ré-envoyée à chaque dépôt — pas de
 * session côté souscripteur, le back re-vérifie systématiquement.
 *
 * Limite back : 10 Mo / fichier (HTTP RequestSizeLimit + check applicatif).
 * On contrôle aussi côté front pour éviter un round-trip inutile.
 */
export const DEPOT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export async function deposerFichier(
  token: string,
  dateNaissance: string,
  file: File
): Promise<DepotDeposerResultatResponse> {
  const form = new FormData();
  form.append('dateNaissance', dateNaissance);
  form.append('file', file);

  const response = await fetch(`${PUBLIC_BASE}/${token}/deposer`, {
    method: 'POST',
    body: form
    // Pas de Content-Type explicite : le navigateur ajoute multipart/form-data
    // avec son boundary tout seul.
  });

  if (!response.ok) {
    await throwBusinessOrGeneric(response);
  }
  return response.json() as Promise<DepotDeposerResultatResponse>;
}

/**
 * 4. Suppression d'un document déjà déposé. La DDN est ré-envoyée pour
 * authentifier — le storedFileId vient de la liste `uploadedDocuments`
 * renvoyée par /authentifier ou /deposer.
 *
 * Le back vérifie que l'ID appartient bien à l'emprunteur ciblé par le
 * token (anti-énumération) — si non, renvoie FileNotFound qu'on traite
 * comme une erreur métier non terminale (la liste est juste désynchro,
 * on la rafraîchit à la prochaine action).
 *
 * Renvoie la liste fraîche pour éviter d'avoir à rappeler /authentifier.
 */
export async function supprimerFichier(
  token: string,
  dateNaissance: string,
  storedFileId: string
): Promise<DepotDeposerResultatResponse> {
  return postJson<DepotDeposerResultatResponse>(
    `${PUBLIC_BASE}/${token}/supprimer/${storedFileId}`,
    { dateNaissance }
  );
}

/** 5. Confirmation finale — invalide définitivement le token. */
export async function confirmerDepot(
  token: string,
  dateNaissance: string
): Promise<DepotOperationResponse> {
  return postJson<DepotOperationResponse>(
    `${PUBLIC_BASE}/${token}/confirmer`,
    { dateNaissance }
  );
}

/**
 * 6. Prévisualisation d'un document déjà déposé.
 *
 * Le back renvoie le binaire en streaming avec le bon Content-Type
 * (application/pdf | image/jpeg | image/png). On récupère le blob côté
 * JS pour ensuite créer une object URL et l'afficher dans une <iframe>
 * ou <img> — POST + body ne pouvant pas être consommé par l'attribut
 * `src` directement.
 *
 * Note : la route est interdite après /confirmer (token nulé → renvoie
 * MEDICAL_FORM_TOKEN_INVALID), comportement géré comme code terminal
 * standard côté page.
 */
export async function visualiserFichier(
  token: string,
  dateNaissance: string,
  storedFileId: string
): Promise<{ blob: Blob; contentType: string }> {
  const response = await fetch(
    `${PUBLIC_BASE}/${token}/visualiser/${storedFileId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dateNaissance })
    }
  );

  if (!response.ok) {
    await throwBusinessOrGeneric(response);
  }

  const blob = await response.blob();
  // Content-Type vient du header HTTP. Fallback sur blob.type au cas où,
  // puis octet-stream — on évite undefined qui casserait le switch côté UI.
  const contentType =
    response.headers.get('content-type') ??
    blob.type ??
    'application/octet-stream';
  return { blob, contentType };
}
